var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
let SelectMultipleInput = class SelectMultipleInput extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.value = [];
        this.variant = 'primary';
        this.size = 'medium';
        this.special = false;
        this.bold = false;
        this.placeholder = '';
        this.isOpen = false;
        this.handleClickOutside = (event) => {
            // Check if the click is inside this component using composedPath
            const path = event.composedPath();
            if (!path.includes(this)) {
                this.closeDropdown();
            }
        };
    }
    toggleDropdown() {
        this.isOpen = !this.isOpen;
        this.requestUpdate();
    }
    closeDropdown() {
        this.isOpen = false;
        this.requestUpdate();
    }
    handleToggleOption(optionValue) {
        const currentValue = this.value || [];
        const index = currentValue.indexOf(optionValue);
        if (index > -1) {
            // Remove option
            this.value = currentValue.filter(v => v !== optionValue);
        }
        else {
            // Add option
            this.value = [...currentValue, optionValue];
        }
        this.dispatchEvent(new CustomEvent('select-multiple-change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
        }));
    }
    handleRemoveTag(optionValue, event) {
        event.stopPropagation();
        this.handleToggleOption(optionValue);
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.handleClickOutside);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.handleClickOutside);
    }
    render() {
        const variantClass = `select-multiple-input--${this.variant}`;
        const sizeClass = `select-multiple-input--${this.size}`;
        const specialClass = this.special ? 'select-multiple-input--special' : '';
        const boldClass = this.bold ? 'select-multiple-input--bold' : '';
        const selectedOptions = this.options.filter(opt => (this.value || []).includes(opt.value));
        return html `
      <div class="select-multiple-container">
        <div
          class="select-multiple-input ${variantClass} ${sizeClass} ${specialClass} ${boldClass}"
          @click="${this.toggleDropdown}"
        >
          ${selectedOptions.length === 0
            ? html `<span class="placeholder">${this.placeholder || msg('Select options...', { id: 'form.placeholder.selectOptions', desc: 'Multiple select placeholder' })}</span>`
            : selectedOptions.map(opt => html `
                <span class="selected-tag">
                  ${opt.label}
                  <button
                    class="remove-tag"
                    @click="${(e) => this.handleRemoveTag(opt.value, e)}"
                    aria-label=${msg(str `Remove ${opt.label}`, { id: 'common.action.removeItem', desc: 'Remove selected item, with item label' })}
                  >
                    ×
                  </button>
                </span>
              `)}
        </div>

        ${this.isOpen ? html `
          <div class="dropdown">
            ${this.options.map(option => {
            const isSelected = (this.value || []).includes(option.value);
            return html `
                <div
                  class="dropdown-option ${isSelected ? 'selected' : ''}"
                  @click="${() => this.handleToggleOption(option.value)}"
                >
                  <input
                    type="checkbox"
                    class="checkbox"
                    .checked="${isSelected}"
                    @click="${(e) => e.stopPropagation()}"
                  />
                  <span>${option.label}</span>
                </div>
              `;
        })}
          </div>
        ` : ''}
      </div>
    `;
    }
};
SelectMultipleInput.styles = css `
    .select-multiple-container {
      width: 100%;
      position: relative;
    }

    .select-multiple-input {
      width: 100%;
      padding: 6px 10px;
      font-size: 14px;
      border-radius: 8px;
      border: 1px solid #ccc;
      background: #fff;
      cursor: pointer;
      transition: border 0.3s, box-shadow 0.3s, background-color 0.3s, color 0.3s;
      min-height: 36px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
      box-sizing: border-box;
    }

    .select-multiple-input:focus-within {
      border-color: #0078d4;
      box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
      outline: none;
    }

    .placeholder {
      color: #999;
      font-style: italic;
    }

    .selected-tag {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 1px 6px;
      border-radius: 4px;
      font-size: 12px;
      background-color: #e0e0e0;
      color: #333;
      line-height: 1.2;
    }

    .remove-tag {
      cursor: pointer;
      font-weight: bold;
      font-size: 14px;
      line-height: 1;
      border: none;
      background: none;
      padding: 0;
      margin: 0;
      color: #666;
    }

    .remove-tag:hover {
      color: #000;
    }

    .dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 8px;
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .dropdown-option {
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background-color 0.2s;
    }

    .dropdown-option:hover {
      background-color: #f5f5f5;
    }

    .dropdown-option.selected {
      background-color: #e3f2fd;
    }

    .checkbox {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }

    /* Variants */
    .select-multiple-input--primary {
      border-color: #0078d4;
    }

    .select-multiple-input--primary .selected-tag {
      background-color: rgba(0, 120, 212, 0.2);
      color: #0078d4;
    }

    .select-multiple-input--secondary {
      border-color: #6c757d;
    }

    .select-multiple-input--secondary .selected-tag {
      background-color: rgba(108, 117, 125, 0.2);
      color: #6c757d;
    }

    .select-multiple-input--success {
      border-color: #28a745;
    }

    .select-multiple-input--success .selected-tag {
      background-color: rgba(40, 167, 69, 0.2);
      color: #28a745;
    }

    .select-multiple-input--danger {
      border-color: #dc3545;
    }

    .select-multiple-input--danger .selected-tag {
      background-color: rgba(220, 53, 69, 0.2);
      color: #dc3545;
    }

    .select-multiple-input--warning {
      border-color: #ffc107;
    }

    .select-multiple-input--warning .selected-tag {
      background-color: rgba(255, 193, 7, 0.2);
      color: #ffc107;
    }

    /* Sizes */
    .select-multiple-input--small {
      font-size: 12px;
      padding: 6px 10px;
      min-height: 32px;
    }

    .select-multiple-input--small .selected-tag {
      font-size: 11px;
      padding: 1px 6px;
    }

    .select-multiple-input--medium {
      font-size: 14px;
      padding: 8px 12px;
      min-height: 38px;
    }

    .select-multiple-input--large {
      font-size: 20px;
      padding: 8px 14px;
      min-height: 47px;
    }

    .select-multiple-input--large .selected-tag {
      font-size: 16px;
      padding: 2px 8px;
    }

    /* Special styles */
    .select-multiple-input--special {
      background-color: rgba(0, 120, 212, 0.1);
    }

    .select-multiple-input--special.select-multiple-input--primary {
      background-color: rgba(0, 120, 212, 0.1);
    }

    .select-multiple-input--special.select-multiple-input--secondary {
      background-color: rgba(108, 117, 125, 0.1);
    }

    .select-multiple-input--special.select-multiple-input--success {
      background-color: rgba(40, 167, 69, 0.1);
    }

    .select-multiple-input--special.select-multiple-input--danger {
      background-color: rgba(220, 53, 69, 0.1);
    }

    .select-multiple-input--special.select-multiple-input--warning {
      background-color: rgba(255, 193, 7, 0.1);
    }

    /* Bold styles */
    .select-multiple-input--bold {
      font-weight: bold;
    }

    .select-multiple-input--bold.select-multiple-input--primary {
      color: #0078d4;
    }

    .select-multiple-input--bold.select-multiple-input--secondary {
      color: #6c757d;
    }

    .select-multiple-input--bold.select-multiple-input--success {
      color: #28a745;
    }

    .select-multiple-input--bold.select-multiple-input--danger {
      color: #dc3545;
    }

    .select-multiple-input--bold.select-multiple-input--warning {
      color: #ffc107;
    }
  `;
__decorate([
    property({ type: Array })
], SelectMultipleInput.prototype, "options", void 0);
__decorate([
    property({ type: Array })
], SelectMultipleInput.prototype, "value", void 0);
__decorate([
    property({ type: String })
], SelectMultipleInput.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], SelectMultipleInput.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], SelectMultipleInput.prototype, "special", void 0);
__decorate([
    property({ type: Boolean })
], SelectMultipleInput.prototype, "bold", void 0);
__decorate([
    property({ type: String })
], SelectMultipleInput.prototype, "placeholder", void 0);
__decorate([
    state()
], SelectMultipleInput.prototype, "isOpen", void 0);
SelectMultipleInput = __decorate([
    localized(),
    customElement('select-multiple-input')
], SelectMultipleInput);
export { SelectMultipleInput };
//# sourceMappingURL=SelectMultipleInput.js.map